import React, { useEffect, useMemo, useState } from 'react';
import { Camera, Loader2, Trash2, Check, X, Sparkles, Users } from 'lucide-react';
import { api } from '../lib/api';
import { useCustomSprites } from '../hooks/useCustomSprites';
import { usePublicSprites } from '../hooks/usePublicSprites';
import { resizeSquareDataUrl } from '../lib/imageResize';
import { toast } from '../hooks/use-toast';

const PICKER_SIZE = 256;

export default function ProfilePictureCard({ user, onSaved }) {
  const [tab, setTab] = useState('forged'); // 'forged' | 'community'
  const [picking, setPicking] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const { sprites: forged } = useCustomSprites();
  const { sprites: community } = usePublicSprites();

  const myCommunity = useMemo(
    () => (community || []).filter((s) => s.owner_id === user?.id),
    [community, user?.id]
  );

  const currentPic = user?.picture;
  const initial = ((user?.name || user?.username || '?')[0] || '?').toUpperCase();

  const pickForged = async (sprite) => {
    if (!sprite?.dataUrl) return;
    setBusyId(sprite.id);
    try {
      const shrunk = await resizeSquareDataUrl(sprite.dataUrl, PICKER_SIZE);
      await api.setMyPicture(shrunk);
      toast({ title: 'Profile picture updated', description: `Set to “${sprite.name || 'Untitled Sprite'}”.` });
      onSaved?.();
      setPicking(false);
    } catch (e) {
      toast({ title: 'Could not update picture', description: e.message || 'Try a smaller image.' });
    } finally { setBusyId(null); }
  };

  const pickCommunity = async (sprite) => {
    if (!sprite?.data_url) return;
    setBusyId(sprite.id);
    try {
      const shrunk = await resizeSquareDataUrl(sprite.data_url, PICKER_SIZE);
      await api.setMyPicture(shrunk);
      toast({ title: 'Profile picture updated', description: `Set to “${sprite.name || 'Untitled Sprite'}”.` });
      onSaved?.();
      setPicking(false);
    } catch (e) {
      toast({ title: 'Could not update picture', description: e.message });
    } finally { setBusyId(null); }
  };

  const removePic = async () => {
    if (!currentPic) return;
    if (!window.confirm('Remove your profile picture?')) return;
    setBusyId('_clear_');
    try {
      await api.clearMyPicture();
      toast({ title: 'Profile picture removed' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Failed to remove', description: e.message });
    } finally { setBusyId(null); }
  };

  return (
    <div
      data-testid="profile-picture-card"
      className="rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064] overflow-hidden"
    >
      <div className="p-3 sm:p-4 bg-[#ffd900] border-b-2 border-[#3a1064]/15 flex items-center gap-2">
        <Camera className="h-4 w-4 text-[#3a1064]" strokeWidth={3} />
        <div className="font-black text-[#3a1064] text-sm">Profile picture</div>
      </div>

      {/* Current avatar row */}
      <div className="p-3 sm:p-4 flex items-center gap-3 flex-wrap">
        <div className="h-20 w-20 rounded-2xl border-[3px] border-[#3a1064] overflow-hidden shrink-0 shadow-[0_4px_0_#3a1064] bg-white">
          {currentPic ? (
            <img src={currentPic} alt="Your profile" className="h-full w-full object-cover" />
          ) : (
            <div
              className="h-full w-full flex items-center justify-center text-3xl font-black text-white"
              style={{ background: 'linear-gradient(135deg,#ffd900 0%,#ff2bd6 55%,#9d3dff 100%)' }}
            >
              {initial}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-black text-[#3a1064]">
            {currentPic ? 'You look great.' : 'No custom picture yet.'}
          </div>
          <div className="text-xs font-bold text-[#3a1064]/70">
            Pick any sprite you forged or shared to the community.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!picking && (
            <button
              data-testid="pick-picture-btn"
              onClick={() => setPicking(true)}
              className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs shadow-[0_3px_0_#3a1064] flex items-center gap-1.5"
            >
              <Camera className="h-3.5 w-3.5" strokeWidth={3} /> Change
            </button>
          )}
          {currentPic && (
            <button
              data-testid="remove-picture-btn"
              onClick={removePic}
              disabled={busyId === '_clear_'}
              className="h-9 px-3 rounded-lg border-2 border-rose-500 bg-white hover:bg-rose-50 text-rose-700 font-black text-xs shadow-[0_3px_0_#7f1d1d] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyId === '_clear_'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Trash2 className="h-3.5 w-3.5" strokeWidth={3} />}
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Picker */}
      {picking && (
        <div className="border-t-2 border-[#3a1064]/15 bg-yellow-50 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <TabButton active={tab === 'forged'} onClick={() => setTab('forged')} testId="tab-forged" icon={<Sparkles className="h-3.5 w-3.5" />}>
              Forged ({forged.length})
            </TabButton>
            <TabButton active={tab === 'community'} onClick={() => setTab('community')} testId="tab-community" icon={<Users className="h-3.5 w-3.5" />}>
              Community ({myCommunity.length})
            </TabButton>
            <button
              onClick={() => setPicking(false)}
              className="ml-auto h-8 w-8 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          </div>

          {tab === 'forged' && (
            <SpriteGrid
              testId="forged-grid"
              items={forged.map((s) => ({ id: s.id, name: s.name, src: s.dataUrl }))}
              emptyHint="You haven't forged a sprite yet. Open the Sprite Forge from the tracker and make one!"
              busyId={busyId}
              onPick={(item) => {
                const s = forged.find((x) => x.id === item.id);
                if (s) pickForged(s);
              }}
            />
          )}

          {tab === 'community' && (
            <SpriteGrid
              testId="community-grid"
              items={myCommunity.map((s) => ({ id: s.id, name: s.name, src: s.data_url }))}
              emptyHint="You haven't published any sprites to the gallery yet."
              busyId={busyId}
              onPick={(item) => {
                const s = myCommunity.find((x) => x.id === item.id);
                if (s) pickCommunity(s);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, testId, icon, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`h-8 px-3 rounded-lg border-2 border-[#3a1064] font-black text-xs flex items-center gap-1.5 ${
        active ? 'bg-[#3a1064] text-yellow-300' : 'bg-white text-[#3a1064] hover:bg-yellow-100'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function SpriteGrid({ items, emptyHint, onPick, busyId, testId }) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-[#3a1064]/30 bg-white/80 p-6 text-center text-xs font-bold text-[#3a1064]/70">
        {emptyHint}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {items.map((it) => (
        <button
          key={it.id}
          data-testid={`pick-sprite-${it.id}`}
          onClick={() => onPick(it)}
          disabled={busyId === it.id}
          className="relative aspect-square rounded-xl border-2 border-[#3a1064] bg-white hover:-translate-y-0.5 hover:shadow-[0_4px_0_#3a1064] transition-transform overflow-hidden group disabled:opacity-60"
        >
          <img src={it.src} alt={it.name || 'Sprite'} className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-[#3a1064]/85 text-white text-[10px] font-black uppercase tracking-wider py-1 px-1 truncate">
            {it.name || 'Untitled'}
          </div>
          {busyId === it.id ? (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#ff2bd6]" />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-[#ff2bd6]/80 transition-opacity">
              <Check className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
